import { useEffect, useState } from "react"

import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import { OrderCustomerInfoResult } from "@/api/shopify-graphql/shopify-graphql.type"
import { formatShippingAddress } from "@/utils/address"

export type OrderCustomerInfo = NonNullable<OrderCustomerInfoResult["order"]> & {
  /** 复制用：按 Shopify 后台语言排版好的收货地址各行（含姓名、公司，不含电话）；页面展示仍用 formatted */
  shippingAddressCopyLines: string[]
}

/**
 * 从 Shopify Admin GraphQL 取订单的客户、联系方式与当前收货地址
 * - info：请求失败或无数据时为 undefined，由调用方回退到后端返回的订单字段
 * - loading：当前订单的请求还没结束（无论成败）时为 true，用于展示骨架屏，避免内容先旧后新地跳变
 */
export default function useOrderCustomerInfo(orderId?: string | number, enabled = true, language = "en") {
  const [result, setResult] = useState<{ orderId: string | number, info?: OrderCustomerInfo }>()

  useEffect(() => {
    if (!enabled || !orderId) {
      return
    }

    // 切换订单时丢弃上一单晚到的结果
    let ignore = false

    ShopifyGraphql.getOrderCustomerInfo(orderId)
      .then(async ({ data }) => {
        const order = data?.order

        if (!order) {
          return undefined
        }

        const shippingAddressCopyLines = order.shippingAddress ? await formatShippingAddress(order.shippingAddress, language) : []

        return { ...order, shippingAddressCopyLines }
      })
      .catch(() => undefined)
      .then(info => {
        if (!ignore) {
          setResult({ orderId, info })
        }
      })

    return () => {
      ignore = true
    }
  }, [orderId, enabled, language])

  // 按「当前订单的结果是否已到」推算，拿到 orderId 的第一帧就是 loading，不会先闪一下后端数据
  const isCurrent = Boolean(enabled && orderId) && result?.orderId === orderId

  return {
    info: isCurrent ? result?.info : undefined,
    loading: Boolean(enabled && orderId) && !isCurrent,
  }
}
